import { Graphics } from "pixi.js"
import { SCALER } from "../../../../client_util"
import { IMouse, MouseState } from "../../../../client_models"
import { BaseRenderable } from "../sprites/base_renderable"

const INK = 0x201b19
const IVORY = 0xfff1d5
const BRASS = 0xd9a65d

/** Screen-space sight: no world projection, aim assistance, or implied accuracy/spread. */
export class TargetingReticle extends BaseRenderable {
    private sight = new Graphics()
    private echo = new Graphics()
    private idleMs = 0
    private focus = 0
    private kick = 0
    private reducedMotion = typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    constructor(private mouse?: IMouse) {
        super()
        this.scale.set(1, 1 / SCALER)
        this.zIndex = 59000
        this.interactive = false
        this.interactiveChildren = false
        this.visible = false
        this.addChild(this.echo, this.sight)
        mouse?.addListener(this.followPointer)
        if (mouse) this.followPointer(mouse.state())
        this.draw()
    }

    private followPointer = (state: MouseState) => {
        if (this.x !== state.position.x || this.y !== state.position.y / SCALER) this.idleMs = 0
        this.position.set(state.position.x, state.position.y / SCALER)
    }

    shot = () => {
        if (!this.visible || !this.renderable) return
        this.idleMs = 0
        this.kick = this.reducedMotion ? 0 : 1
    }

    animate = (deltaMs: number, held: boolean, suppressed: boolean) => {
        this.renderable = !suppressed
        if (!this.visible || suppressed) {
            const changed = this.kick !== 0 || this.focus !== 0
            this.kick = 0
            this.focus = 0
            if (changed) this.draw()
            return
        }
        const dt = Math.max(0, Math.min(deltaMs, 64))
        this.idleMs += dt
        const nextFocus = held ? 1 : 0
        const previousFocus = this.focus
        const previousKick = this.kick
        this.focus = this.reducedMotion ? nextFocus : this.focus + (nextFocus - this.focus) * (1 - Math.exp(-dt / 85))
        if (Math.abs(this.focus - nextFocus) < 0.001) this.focus = nextFocus
        this.kick = Math.max(0, this.kick - dt / 240)
        // Keep the aim point legible while still; no looping pulse or rotation.
        const targetAlpha = held || this.idleMs < 900 ? 1 : 0.88
        this.alpha += (targetAlpha - this.alpha) * (1 - Math.exp(-dt / 160))
        if (previousFocus !== this.focus || previousKick !== this.kick) this.draw()
    }

    private draw = () => {
        const sight = this.sight
        sight.clear()
        const radius = 16 - this.focus * 2 + this.kick * 3
        // Four separated, inward-facing corners leave the target unobscured. The dark
        // under-stroke keeps the fine warm lines readable over snow, grass and fire.
        for (const [width, color, alpha] of [[5.5, INK, 0.95], [2.5, IVORY, 1]]) {
            sight.lineStyle(width, color, alpha)
            for (const [x, y] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
                sight.moveTo(x * (radius - 6), y * radius)
                sight.lineTo(x * radius, y * radius)
                sight.lineTo(x * radius, y * (radius - 6))
            }
        }
        sight.lineStyle(3.5, INK, 0.95)
        sight.moveTo(0, -radius - 4).lineTo(0, -radius - 1)
        sight.moveTo(0, radius + 1).lineTo(0, radius + 4)
        sight.lineStyle(1.5, BRASS, 1)
        sight.moveTo(0, -radius - 4).lineTo(0, -radius - 1)
        sight.moveTo(0, radius + 1).lineTo(0, radius + 4)
        sight.lineStyle(0)
        sight.beginFill(INK, 0.95).drawPolygon([0, -3.5, 3.5, 0, 0, 3.5, -3.5, 0]).endFill()
        sight.beginFill(IVORY).drawPolygon([0, -2, 2, 0, 0, 2, -2, 0]).endFill()
        // A single short-lived release echo, only after a projectile was created.
        this.echo.clear()
        if (this.kick > 0) {
            this.echo.lineStyle(1, IVORY, this.kick * 0.55)
            const r = 20 + (1 - this.kick) * 8
            for (let i = 0; i < 4; i++) {
                const angle = i * Math.PI / 2
                this.echo.moveTo(Math.cos(angle + 0.4) * r, Math.sin(angle + 0.4) * r)
                this.echo.arc(0, 0, r, angle + 0.4, angle + 1.17)
            }
        }
    }

    destroy(options?: Parameters<BaseRenderable["destroy"]>[0]) {
        this.mouse?.removeListener(this.followPointer)
        super.destroy(options)
    }
}
